/**
 * As câmeras da Operação, com os controles de imagem que a conferência pede.
 *
 * O mockup da Gisele (tela 3) tem brilho, contraste, zoom, recorte e o zoom da placa. Tudo
 * aqui é **só de exibição**: filtro CSS e transformação sobre o `<img>`. Nenhum controle
 * grava imagem nova nem altera a evidência — a evidência é o arquivo com hash no banco, e
 * uma "versão clareada" salva por cima seria outra evidência, sem cadeia de custódia.
 *
 * **As três câmeras sempre aparecem** (`C-06`), mesmo a que falhou: esconder a
 * indisponível faria o operador concluir que o pórtico tem duas. Um trânsito que nasceu de
 * upload tem uma imagem só — e o visor diz isso, em vez de desenhar duas molduras vazias
 * que pareceriam câmeras quebradas.
 *
 * **Recortar é enquadrar.** Clicar na imagem ampliada põe o ponto clicado no centro do
 * zoom; é o que o operador faz com o dedo numa foto de celular, e é o que resolve a placa
 * pequena no canto da imagem lateral.
 */
import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";

import { imagemDaAnalise, rotuloDaVista, veioDeUpload } from "../api/tipos";
import type { DetalheDoTransito, ResultadoDaCapacidade } from "../api/tipos";
import { Badge, Botao, Estado, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { camera as nomeDaCamera, corDaConfianca, percentual } from "../formato";
import { grupoDaClasse } from "../leituras";
import { motivoDaFaltaDeAnonimizacao } from "./diagnosticoDasIas";
import { LeituraDoPortico } from "./LeituraDoPortico";

const COR_DA_CONFIANCA = {
  verde: "text-verde",
  amarelo: "text-amarelo",
  vermelho: "text-vermelho",
} as const;

interface Fonte {
  chave: string;
  rotulo: string;
  papel: string;
  src: string | null;
  placa: string | null;
  confianca: number | null;
  motivo: string | null;
}

const PAPEL: Record<string, string> = {
  FRONTAL: "OCR primário",
  LATERAL: "silhueta",
  EIXO: "contagem de eixos",
};

/** As imagens de um trânsito, na ordem em que o operador as confere.
 *
 * Três origens, e as três mostram **só o que pode ser mostrado**: as câmeras do pórtico; as
 * vistas de um veículo que veio por upload (0022 — frente, lateral, traseira no mesmo
 * trânsito); e o formato antigo de upload, uma foto por trânsito. Foto de upload aparece
 * **anonimizada** (feedback de 21/09): sem cópia anônima garantida, a moldura diz por quê e o
 * original não é exibido.
 */
function fontesDoTransito(
  detalhe: DetalheDoTransito,
  analiseDasVistas: Record<string, ResultadoDaCapacidade[]> = {},
): Fonte[] {
  if (detalhe.imagens_de_analise.length > 0) {
    return detalhe.imagens_de_analise.map((i) => ({
      chave: i.imagem_id,
      rotulo: rotuloDaVista(i.vista),
      papel: grupoDaClasse(i.classe)?.toLowerCase() ?? "upload",
      src: i.anonimizada ? `/analise/imagens/${i.imagem_id}/anonimizada` : null,
      placa: i.placa,
      // A análise guarda fração (0–1); a tela fala em %.
      confianca: i.placa_confianca == null ? null : i.placa_confianca * 100,
      motivo: i.anonimizada
        ? null
        : (motivoDaFaltaDeAnonimizacao(analiseDasVistas[i.imagem_id]) ??
          "aguardando anonimização — o original não é exibido"),
    }));
  }
  const analise = imagemDaAnalise(detalhe);
  if (analise) {
    return [
      {
        chave: "UPLOAD",
        rotulo: "Imagem enviada",
        papel: "upload",
        src: `/analise/imagens/${analise}/anonimizada`,
        placa: detalhe.placa,
        confianca: detalhe.confianca_ponderada,
        motivo: null,
      },
    ];
  }
  return ["FRONTAL", "LATERAL", "EIXO"].map((nome) => {
    const leitura = detalhe.leituras.find((l) => l.camera === nome);
    const disponivel = leitura?.disponivel === true && !!leitura.imagem_id;
    return {
      chave: nome,
      rotulo: nomeDaCamera(nome),
      papel: PAPEL[nome] ?? "",
      src: disponivel ? `/tollvision/imagens/${leitura!.imagem_id}` : null,
      placa: leitura?.placa ?? null,
      confianca: leitura?.confianca ?? null,
      motivo:
        leitura?.motivo_indisponibilidade ??
        (leitura ? null : "sem registro desta câmera"),
    };
  });
}

interface Ajuste {
  brilho: number;
  contraste: number;
  zoom: number;
  origem: { x: number; y: number };
}

const NEUTRO: Ajuste = {
  brilho: 100,
  contraste: 100,
  zoom: 1,
  origem: { x: 50, y: 50 },
};
/** Onde a placa costuma estar numa imagem frontal de pórtico: centro, terço de baixo. */
const REGIAO_DA_PLACA: Ajuste = {
  ...NEUTRO,
  zoom: 2.6,
  contraste: 130,
  origem: { x: 50, y: 74 },
};

/** A caixa da placa em pixels da imagem original, quando a IA a devolveu (hoje: OCR da
 *  análise por upload). Com ela, o "Zoom da placa" vai aonde a placa está — sem ela, vai
 *  aonde a placa costuma estar numa frontal de pórtico. */
export interface CaixaDaPlaca {
  x: number;
  y: number;
  largura: number;
  altura: number;
}

export function Visor({
  detalhe,
  caixaDaPlaca = null,
  analiseDasVistas = {},
}: {
  detalhe: DetalheDoTransito | null;
  caixaDaPlaca?: CaixaDaPlaca | null;
  /** O que as IAs disseram sobre cada vista: diz por que a foto ainda não aparece (03/11). */
  analiseDasVistas?: Record<string, ResultadoDaCapacidade[]>;
}) {
  const [foco, definirFoco] = useState(0);
  const [tamanho, definirTamanho] = useState<{ w: number; h: number } | null>(
    null,
  );
  const [ajuste, definirAjuste] = useState<Ajuste>(NEUTRO);
  const [telaCheia, definirTelaCheia] = useState(false);

  // Trânsito novo, olhar novo: carregar o brilho da placa anterior faria a imagem seguinte
  // parecer defeituosa — e o operador ajustaria de novo por cima de um ajuste que não fez.
  const id = detalhe?.id;
  useEffect(() => {
    definirFoco(0);
    definirAjuste(NEUTRO);
    definirTelaCheia(false);
  }, [id]);

  // Atalhos: P zoom da placa, F tela cheia, Esc sai, 1–9 e ← → trocam a foto. O ref leva para
  // o ouvinte o que só existe depois do `return` antecipado (as fontes, a região da placa).
  const atalhos = useRef<{ placa: () => void; cameras: number } | null>(null);
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const alvo = e.target;
      if (
        alvo instanceof HTMLElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(alvo.tagName)
      )
        return;
      const tecla = e.key.toLowerCase();
      if (tecla === "escape") definirTelaCheia(false);
      if (!atalhos.current) return;
      if (tecla === "p") {
        e.preventDefault();
        atalhos.current.placa();
      } else if (tecla === "f") {
        e.preventDefault();
        definirTelaCheia((t) => !t);
      } else if (
        /^[1-9]$/.test(tecla) &&
        Number(tecla) <= atalhos.current.cameras
      ) {
        definirFoco(Number(tecla) - 1);
        definirAjuste(NEUTRO);
      } else if (tecla === "arrowright" || tecla === "arrowleft") {
        // O carrossel (Gisele, 21/09): ← → passa as fotos do mesmo veículo. ↑↓ é da fila.
        const n = atalhos.current.cameras;
        if (n < 2) return;
        e.preventDefault();
        const passo = tecla === "arrowright" ? 1 : -1;
        definirFoco((f) => (Math.min(f, n - 1) + passo + n) % n);
        definirAjuste(NEUTRO);
      }
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, []);

  if (!detalhe) {
    atalhos.current = null;
    return (
      <Painel titulo="Imagens">
        <Estado
          tipo="vazio"
          titulo="Selecione um trânsito na fila para conferir as imagens."
        />
      </Painel>
    );
  }

  const fontes = fontesDoTransito(detalhe, analiseDasVistas);
  const atual = fontes[Math.min(foco, fontes.length - 1)]!;
  // A caixa vale para a imagem em que foi lida: a primeira (frontal, ou a única do upload).
  const regiaoDaPlaca: Ajuste =
    caixaDaPlaca && tamanho && foco === 0
      ? {
          ...NEUTRO,
          contraste: 130,
          zoom: Math.min(
            4,
            Math.max(1.6, (0.3 * tamanho.w) / caixaDaPlaca.largura),
          ),
          origem: {
            x: ((caixaDaPlaca.x + caixaDaPlaca.largura / 2) / tamanho.w) * 100,
            y: ((caixaDaPlaca.y + caixaDaPlaca.altura / 2) / tamanho.h) * 100,
          },
        }
      : REGIAO_DA_PLACA;
  const ajustado =
    ajuste.brilho !== 100 || ajuste.contraste !== 100 || ajuste.zoom !== 1;
  atalhos.current = {
    placa: () => definirAjuste(regiaoDaPlaca),
    cameras: fontes.length,
  };

  function enquadrar(evento: MouseEvent<HTMLElement>) {
    const caixa = evento.currentTarget.getBoundingClientRect();
    const x = ((evento.clientX - caixa.left) / caixa.width) * 100;
    const y = ((evento.clientY - caixa.top) / caixa.height) * 100;
    definirAjuste((a) => ({
      ...a,
      zoom: Math.max(a.zoom, 2),
      origem: { x, y },
    }));
  }

  const imagem = (grande: boolean) => (
    <Ampliada
      fonte={atual}
      ajuste={ajuste}
      aoClicar={enquadrar}
      aoCarregar={definirTamanho}
      grande={grande}
    />
  );

  return (
    <Painel
      titulo={detalhe.placa ?? "Imagens"}
      acoes={
        <span className="text-[11px] text-tinta-3">
          {detalhe.praca} · faixa {detalhe.faixa} ·{" "}
          {fontes.length === 1
            ? "1 foto"
            : `${fontes.length} ${veioDeUpload(detalhe) ? "fotos do veículo" : "câmeras"}`}
        </span>
      }
      rodape={
        <span>
          Os ajustes são só de exibição: a evidência gravada não muda.
        </span>
      }
    >
      {/* O carrossel: as fotos do mesmo veículo passam aqui, com setas, o contador e ← →.
          Um veículo é um registro na fila; as vistas dele são este carrossel (21/09). */}
      <div className="relative">
        {imagem(false)}
        {fontes.length > 1 && (
          <>
            <span
              data-tema="escuro"
              className="mono pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-botao border border-linha bg-mesa/85 px-2 py-0.5 text-[11px] text-tinta"
            >
              {Math.min(foco, fontes.length - 1) + 1} / {fontes.length} ·{" "}
              {atual.rotulo}
            </span>
            {[-1, 1].map((passo) => (
              <button
                key={passo}
                type="button"
                data-tema="escuro"
                aria-label={passo < 0 ? "Foto anterior" : "Próxima foto"}
                onClick={() => {
                  const n = fontes.length;
                  definirFoco((f) => (Math.min(f, n - 1) + passo + n) % n);
                  definirAjuste(NEUTRO);
                }}
                className={`absolute top-1/2 -translate-y-1/2 ${
                  passo < 0 ? "left-2" : "right-2"
                } grid size-9 place-items-center rounded-full border border-linha bg-mesa/80 text-[18px] leading-none text-tinta hover:bg-realce`}
              >
                {passo < 0 ? "‹" : "›"}
              </button>
            ))}
          </>
        )}
      </div>

      <Controles
        ajuste={ajuste}
        ajustado={ajustado}
        regiaoDaPlaca={regiaoDaPlaca}
        aoAjustar={definirAjuste}
        aoTelaCheia={() => definirTelaCheia(true)}
      />

      {fontes.length > 1 && (
        <div
          className="grid grid-cols-[repeat(auto-fill,minmax(96px,1fr))] gap-2"
          role="tablist"
          aria-label="Fotos do veículo"
        >
          {fontes.map((f, i) => (
            <Miniatura
              key={f.chave}
              fonte={f}
              ativa={i === foco}
              aoEscolher={() => {
                definirFoco(i);
                definirAjuste(NEUTRO);
              }}
            />
          ))}
        </div>
      )}

      <LeituraDoPortico detalhe={detalhe} />

      {telaCheia && (
        <div
          role="dialog"
          aria-label={`Imagem ${atual.rotulo} em tela cheia`}
          data-tema="escuro"
          className="fixed inset-0 z-50 grid grid-rows-[auto_1fr_auto] gap-3 bg-mesa p-4"
        >
          <div className="flex items-center gap-2">
            <b className="mono text-tinta">{detalhe.placa ?? "—"}</b>
            <Badge cor="azul">{atual.rotulo}</Badge>
            <span className="ml-auto" />
            <Botao onClick={() => definirTelaCheia(false)}>
              <Icone.Barrado />
              Fechar
            </Botao>
          </div>
          <div className="grid min-h-0 place-items-center">{imagem(true)}</div>
          <Controles
            ajuste={ajuste}
            ajustado={ajustado}
            regiaoDaPlaca={regiaoDaPlaca}
            aoAjustar={definirAjuste}
          />
        </div>
      )}
    </Painel>
  );
}

function Ampliada({
  fonte,
  ajuste,
  aoClicar,
  aoCarregar,
  grande,
}: {
  fonte: Fonte;
  ajuste: Ajuste;
  aoClicar: (e: MouseEvent<HTMLElement>) => void;
  aoCarregar: (t: { w: number; h: number }) => void;
  grande: boolean;
}) {
  return (
    <figure
      className={`grid overflow-hidden rounded-cartao border border-linha bg-painel-2 ${
        grande
          ? "h-full max-h-[78dvh] w-full max-w-[1400px] grid-rows-[auto_1fr]"
          : ""
      }`}
    >
      <figcaption className="flex items-center justify-between gap-2 border-b border-linha px-2 py-1.5 text-[9.5px] tracking-[0.06em] uppercase">
        <span className="text-tinta-2">{fonte.rotulo}</span>
        <span className="text-tinta-3">{fonte.papel}</span>
      </figcaption>
      <div
        data-tema="escuro"
        className={`relative grid place-items-center overflow-hidden bg-mesa ${
          grande
            ? "min-h-0"
            : "h-[46vh] min-h-[240px] xl:h-[calc(100dvh-480px)] xl:min-h-[240px]"
        }`}
      >
        {fonte.src ? (
          // `absolute inset-0` e não altura em porcentagem: foto em retrato (o dataset de
          // teste tem várias) crescia além da moldura e era cortada embaixo — a placa ia
          // junto. Assim a imagem cabe inteira, qualquer que seja a proporção.
          <button
            type="button"
            onClick={aoClicar}
            title="Clique para enquadrar este ponto"
            aria-label="Enquadrar o ponto clicado"
            className="absolute inset-0 cursor-zoom-in"
          >
            <img
              src={fonte.src}
              alt={`Imagem ${fonte.rotulo}`}
              draggable={false}
              onError={(e) => {
                // 409 da cópia anônima: não há garantia — some a foto, fica o aviso.
                e.currentTarget.style.display = "none";
              }}
              onLoad={(e) =>
                aoCarregar({
                  w: e.currentTarget.naturalWidth,
                  h: e.currentTarget.naturalHeight,
                })
              }
              className="h-full w-full object-contain transition-transform duration-150"
              style={{
                filter: `brightness(${ajuste.brilho}%) contrast(${ajuste.contraste}%)`,
                transform: `scale(${ajuste.zoom})`,
                transformOrigin: `${ajuste.origem.x}% ${ajuste.origem.y}%`,
              }}
            />
          </button>
        ) : (
          <span className="grid gap-1 p-4 text-center">
            <span className="text-amarelo">imagem não exibida</span>
            <span className="text-[11px] text-tinta-3">
              {fonte.motivo ?? "sem imagem"}
            </span>
          </span>
        )}
        {/* A leitura da IA em cima da imagem que ela leu: o operador compara sem desviar o
            olho para o painel da direita. */}
        {fonte.src && fonte.placa && (
          <span className="mono pointer-events-none absolute bottom-2 left-2 rounded-botao border border-linha bg-mesa/85 px-2 py-1 text-[12px] text-tinta">
            IA: <b>{fonte.placa}</b>
            {fonte.confianca != null && (
              <span
                className={`ml-1.5 ${COR_DA_CONFIANCA[corDaConfianca(fonte.confianca)]}`}
              >
                {percentual(fonte.confianca)}
              </span>
            )}
          </span>
        )}
      </div>
    </figure>
  );
}

function Controles({
  ajuste,
  ajustado,
  regiaoDaPlaca,
  aoAjustar,
  aoTelaCheia,
}: {
  ajuste: Ajuste;
  regiaoDaPlaca: Ajuste;
  ajustado: boolean;
  aoAjustar: (a: Ajuste | ((atual: Ajuste) => Ajuste)) => void;
  aoTelaCheia?: () => void;
}) {
  return (
    <div className="grid gap-2 rounded-cartao border border-linha bg-painel-2 p-2.5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Regulagem
          rotulo="Brilho"
          valor={ajuste.brilho}
          min={40}
          max={220}
          sufixo="%"
          aoMudar={(v) => aoAjustar((a) => ({ ...a, brilho: v }))}
        />
        <Regulagem
          rotulo="Contraste"
          valor={ajuste.contraste}
          min={40}
          max={220}
          sufixo="%"
          aoMudar={(v) => aoAjustar((a) => ({ ...a, contraste: v }))}
        />
        <Regulagem
          rotulo="Zoom"
          valor={Math.round(ajuste.zoom * 100)}
          min={100}
          max={400}
          sufixo="%"
          aoMudar={(v) => aoAjustar((a) => ({ ...a, zoom: v / 100 }))}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Botao onClick={() => aoAjustar(regiaoDaPlaca)}>
          <Icone.Lupa />
          Zoom da placa
          <kbd aria-hidden className="mono ml-1 text-[9.5px] opacity-60">
            P
          </kbd>
        </Botao>
        {aoTelaCheia && (
          <Botao onClick={aoTelaCheia}>
            <Icone.Expandir />
            Tela cheia
            <kbd aria-hidden className="mono ml-1 text-[9.5px] opacity-60">
              F
            </kbd>
          </Botao>
        )}
        <Botao disabled={!ajustado} onClick={() => aoAjustar(NEUTRO)}>
          <Icone.Recarga />
          Restaurar
        </Botao>
        <span className="self-center text-[10.5px] text-tinta-3">
          Clique na imagem para enquadrar · ← → ou 1–9 trocam a foto.
        </span>
      </div>
    </div>
  );
}

function Regulagem({
  rotulo,
  valor,
  min,
  max,
  sufixo,
  aoMudar,
}: {
  rotulo: string;
  valor: number;
  min: number;
  max: number;
  sufixo: string;
  aoMudar: (v: number) => void;
}) {
  return (
    <label className="grid min-w-0 gap-0.5 text-[10.5px] text-tinta-3">
      <span className="flex justify-between">
        <span>{rotulo}</span>
        <span className="mono text-tinta-2">
          {valor}
          {sufixo}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        value={valor}
        aria-label={rotulo}
        onChange={(e) => aoMudar(Number(e.target.value))}
        className="w-full min-w-0 accent-azul"
      />
    </label>
  );
}

function Miniatura({
  fonte,
  ativa,
  aoEscolher,
}: {
  fonte: Fonte;
  ativa: boolean;
  aoEscolher: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={ativa}
      onClick={aoEscolher}
      className={`grid overflow-hidden rounded-cartao border text-left ${
        ativa ? "border-azul" : "border-linha"
      } bg-painel-2`}
    >
      <span className="flex items-center justify-between px-1.5 py-1 text-[9px] tracking-[0.06em] text-tinta-2 uppercase">
        {fonte.rotulo}
      </span>
      <span data-tema="escuro" className="grid h-20 place-items-center bg-mesa">
        {fonte.src ? (
          <img
            src={fonte.src}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-[10px] text-vermelho">indisponível</span>
        )}
      </span>
      <span className="mono flex items-center justify-between gap-1 px-1.5 py-1 text-[10.5px]">
        <span className="truncate text-tinta-2">{fonte.placa ?? "—"}</span>
        {fonte.src ? (
          <span className={COR_DA_CONFIANCA[corDaConfianca(fonte.confianca)]}>
            {percentual(fonte.confianca)}
          </span>
        ) : (
          <span className="text-vermelho">sem leitura</span>
        )}
      </span>
    </button>
  );
}
